import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { DOCUMENT_TYPE_BY_CODE, extensionOf, getDocumentType, isExtensionAllowed } from '@erp-attach/shared';
import type { DocumentTypeDefinition } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';
import { ApiRequestError } from '../api/client';
import type { CompleteUploadResponse } from '../api/types';
import { formatBytes } from '../lib/format';
import { uploadFile } from '../lib/upload';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { FilePlate } from './FilePlate';

export interface UploadQueueHandle {
  /** Queue files from a drop or a picker. Validation happens before any network call. */
  addFiles: (files: FileList | File[]) => void;
  /** Open the native file picker. */
  openPicker: () => void;
}

export interface UploadQueueProps {
  folderId: string;
  /** Document types this folder accepts (already filtered by the caller's upload permission). */
  documentTypes: readonly string[];
  disabled?: boolean;
  onUploaded?: (result: CompleteUploadResponse, file: File) => void;
}

type Phase = 'needs-type' | 'pending' | 'uploading' | 'done' | 'error' | 'cancelled';

interface Item {
  id: string;
  file: File;
  typeCode: string | null;
  candidates: string[];
  phase: Phase;
  progress: number;
  message?: string;
  status?: string;
}

/** Client-side policy check mirroring the API (spec §6 step 1). Returns a friendly message or null. */
export function validateFile(dt: DocumentTypeDefinition, file: File): string | null {
  if (file.size <= 0) return `${file.name} is empty.`;
  if (!isExtensionAllowed(dt, file.name)) {
    const ext = extensionOf(file.name) || 'no extension';
    const allowed = dt.allowedExt.map((e) => e.toUpperCase()).join(', ');
    return `${dt.label} accepts ${allowed} files only — "${file.name}" is ${ext.toUpperCase()}.`;
  }
  if (file.size > dt.maxBytes) {
    return `${file.name} is ${formatBytes(file.size)}; the limit for ${dt.label} is ${formatBytes(dt.maxBytes)}.`;
  }
  return null;
}

function friendlyError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') return 'Upload cancelled.';
  if (err instanceof ApiRequestError) {
    if (err.status === 429) return 'You have hit the upload limit for now. Please try again in a few minutes.';
    if (err.status === 413) return 'The file is larger than the server allows for this document type.';
    if (err.status === 403) return 'You do not have permission to upload here.';
    return `${err.message} (ref ${err.correlationId})`;
  }
  return err instanceof Error ? err.message : 'Upload failed.';
}

let seq = 0;
const nextId = () => `u${Date.now().toString(36)}-${(seq += 1)}`;

/**
 * One queue per folder. Files are typed automatically when exactly one allowed type accepts the
 * extension; otherwise the row asks for a type before it starts. Uploads run one at a time.
 */
export const UploadQueue = forwardRef<UploadQueueHandle, UploadQueueProps>(function UploadQueue(
  { folderId, documentTypes, disabled = false, onUploaded },
  ref,
) {
  const api = useApi();
  const inputRef = useRef<HTMLInputElement>(null);
  const controllers = useRef(new Map<string, AbortController>());
  const [items, setItems] = useState<Item[]>([]);
  const types = useMemo(() => documentTypes.map((c) => getDocumentType(c)), [documentTypes]);
  const accept = useMemo(() => Array.from(new Set(types.flatMap((t) => t.allowedExt))).map((e) => `.${e}`).join(','), [types]);

  const patch = useCallback((id: string, changes: Partial<Item>) => {
    setItems((list) => list.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0 || disabled) return;
      const fresh: Item[] = list.map((file) => {
        const candidates = types.filter((t) => isExtensionAllowed(t, file.name)).map((t) => t.code);
        if (candidates.length === 0) {
          const ext = extensionOf(file.name) || 'no extension';
          const allowed = Array.from(new Set(types.flatMap((t) => t.allowedExt.map((e) => e.toUpperCase())))).join(', ');
          return {
            id: nextId(),
            file,
            typeCode: null,
            candidates,
            phase: 'error',
            progress: 0,
            message: `${ext.toUpperCase()} files are not accepted in this folder. It takes ${allowed || 'no file types'}.`,
          };
        }
        if (candidates.length === 1) {
          const dt = getDocumentType(candidates[0]!);
          const problem = validateFile(dt, file);
          return { id: nextId(), file, typeCode: dt.code, candidates, phase: problem ? 'error' : 'pending', progress: 0, message: problem ?? undefined };
        }
        return { id: nextId(), file, typeCode: null, candidates, phase: 'needs-type', progress: 0 };
      });
      setItems((prev) => [...prev, ...fresh]);
    },
    [types, disabled],
  );

  useImperativeHandle(ref, () => ({ addFiles, openPicker: () => inputRef.current?.click() }), [addFiles]);

  // Runner: one upload at a time, in queue order.
  useEffect(() => {
    if (items.some((it) => it.phase === 'uploading')) return;
    const next = items.find((it) => it.phase === 'pending' && it.typeCode);
    if (!next) return;
    const controller = new AbortController();
    controllers.current.set(next.id, controller);
    patch(next.id, { phase: 'uploading', progress: 0 });
    uploadFile(
      api,
      { folderId, documentTypeCode: next.typeCode!, file: next.file },
      (progress) => patch(next.id, { progress }),
      controller.signal,
    )
      .then((result) => {
        patch(next.id, { phase: 'done', progress: 1, status: result.status });
        onUploaded?.(result, next.file);
      })
      .catch((err: unknown) => {
        patch(next.id, { phase: controller.signal.aborted ? 'cancelled' : 'error', message: friendlyError(err) });
      })
      .finally(() => controllers.current.delete(next.id));
  }, [items, api, folderId, onUploaded, patch]);

  const chooseType = (item: Item, code: string) => {
    const dt = getDocumentType(code);
    const problem = validateFile(dt, item.file);
    patch(item.id, { typeCode: code, phase: problem ? 'error' : 'pending', message: problem ?? undefined });
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const finished = items.filter((it) => it.phase === 'done' || it.phase === 'cancelled' || it.phase === 'error');
  const active = items.length - finished.length;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="sr-only"
        disabled={disabled}
        onChange={onInputChange}
        aria-label="Choose files to upload"
        data-testid="upload-input"
      />
      {items.length > 0 ? (
        <section className="gsl-queue" aria-label="Uploads">
          <div className="flex items-center justify-between gap-3 px-4 py-2 text-xs text-muted-foreground">
            <span role="status" aria-live="polite">
              {active > 0 ? `${active} upload${active === 1 ? '' : 's'} in progress` : `${finished.length} finished`}
            </span>
            {finished.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setItems((l) => l.filter((it) => !finished.includes(it)))}>
                Clear finished
              </Button>
            ) : null}
          </div>
          <ul>
            {items.map((it) => (
              <li key={it.id} className="gsl-queue-row" data-phase={it.phase}>
                <FilePlate filename={it.file.name} mime={it.file.type} />
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium">{it.file.name}</span>
                    <span className="gsl-num shrink-0 text-xs text-muted-foreground">{formatBytes(it.file.size)}</span>
                  </div>
                  {it.phase === 'needs-type' ? (
                    <label className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-ochre-text">File as</span>
                      <select
                        className="gsl-input gsl-select w-auto py-1 text-xs"
                        defaultValue=""
                        aria-label={`Document type for ${it.file.name}`}
                        onChange={(e) => e.target.value && chooseType(it, e.target.value)}
                      >
                        <option value="" disabled>
                          Choose a document type
                        </option>
                        {it.candidates.map((c) => (
                          <option key={c} value={c}>
                            {DOCUMENT_TYPE_BY_CODE.get(c)?.label ?? c}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : it.phase === 'uploading' ? (
                    <div className="mt-1.5">
                      <div
                        className="gsl-progress"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(it.progress * 100)}
                        aria-label={`Uploading ${it.file.name}`}
                      >
                        <span style={{ width: `${Math.round(it.progress * 100)}%` }} />
                      </div>
                    </div>
                  ) : (
                    <p role="status" aria-live="polite" className="mt-0.5 text-xs">
                      {it.phase === 'error' ? (
                        <span className="inline-flex items-start gap-1 text-destructive">
                          <Icon name="alert" size={14} className="mt-0.5 shrink-0" /> {it.message}
                        </span>
                      ) : it.phase === 'done' ? (
                        <span className="inline-flex items-center gap-1 text-pine">
                          <Icon name="check" size={14} /> Uploaded as {typeLabel(it.typeCode)} —{' '}
                          {it.status === 'scanning' ? 'scanning for malware' : it.status}.
                        </span>
                      ) : it.phase === 'cancelled' ? (
                        <span className="text-muted-foreground">Cancelled.</span>
                      ) : (
                        <span className="text-muted-foreground">Waiting as {typeLabel(it.typeCode)}</span>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {it.phase === 'uploading' ? (
                    <span className="gsl-num mr-1 text-xs text-muted-foreground">{Math.round(it.progress * 100)}%</span>
                  ) : null}
                  {it.phase === 'uploading' || it.phase === 'pending' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="x"
                      iconOnly
                      aria-label={`Cancel ${it.file.name}`}
                      onClick={() => {
                        controllers.current.get(it.id)?.abort();
                        if (it.phase === 'pending') patch(it.id, { phase: 'cancelled' });
                      }}
                    />
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="x"
                      iconOnly
                      aria-label={`Dismiss ${it.file.name}`}
                      onClick={() => setItems((l) => l.filter((x) => x.id !== it.id))}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
});

function typeLabel(code: string | null): string {
  return code ? (DOCUMENT_TYPE_BY_CODE.get(code)?.label ?? code) : 'document';
}
